import React, { useState } from 'react';
import { KioskHeader, formatPhone, KioskKeypad } from './KioskFlow';
import useIdleTimeout, { IdleWarningModal } from './useIdleTimeout';
import KioskCheckOut from './KioskCheckOut';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const REBOOK_TIMEOUTS = {
  phone: 10,
  name: 30,
  not_found: 15,
};

export default function KioskRebook({ onBack, centerId, employee, terminal }) {
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [guest, setGuest] = useState(null);
  const [lastVisit, setLastVisit] = useState(null);

  function handleIdleReset() { onBack(); }

  const { showWarning, countdown, dismissWarning } = useIdleTimeout({
    timeoutSeconds: step === 'rebook' ? 0 : (REBOOK_TIMEOUTS[step] || 0),
    onReset: handleIdleReset,
    autoReset: step === 'phone' || step === 'name',
  });

  async function lookupLastVisit(overrideFirstName) {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ phone, centerId });
      if (overrideFirstName) params.set('firstName', overrideFirstName);
      const res = await fetch(`${API_URL}/api/kiosk/last-visit?${params}`);
      const data = await res.json();

      if (data.multipleMatches && !overrideFirstName) {
        setStep('name');
        return;
      }
      if (!data.found) {
        setError(data.error || 'No guest found with this phone number.');
        return;
      }

      setGuest(data.guest);

      if (!data.lastVisit || !data.lastVisit.services?.length) {
        setStep('not_found');
        return;
      }

      setLastVisit(data.lastVisit);
      setStep('rebook');
    } catch (err) {
      setError(err.message || 'Could not look up your account.');
    } finally {
      setLoading(false);
    }
  }

  // Hand off to KioskCheckOut with rebook data
  if (step === 'rebook' && guest && lastVisit) {
    return (
      <KioskCheckOut
        onBack={onBack}
        centerId={centerId}
        employee={employee}
        terminal={terminal}
        initialRebook={{
          guest,
          appointment: {
            services: lastVisit.services,
            provider: lastVisit.provider,
            time: lastVisit.time,
          },
        }}
      />
    );
  }

  return (
    <div className="kiosk-container">
      <KioskHeader />

      <div className="kiosk-main">

        {/* PHONE INPUT */}
        {step === 'phone' && (
          <div className="kiosk-screen kiosk-phone-screen kiosk-fade-in" style={{ flex: 'none' }}>
            <div className="kiosk-screen-title">Enter your phone number</div>
            <div className="kiosk-screen-sub">We'll pull up your last visit</div>
            <input
              className="kiosk-phone-input"
              type="tel"
              placeholder="(604) 555-1234"
              value={formatPhone(phone)}
              readOnly
            />
            <KioskKeypad
              mode="numeric"
              value={phone}
              onChange={v => setPhone(v.slice(0, 10))}
              maxLength={10}
            />
            {error && <div style={{ color: '#c44', fontSize: 13, textAlign: 'center' }}>{error}</div>}
            <button className="kiosk-btn-primary" disabled={phone.length < 10 || loading} onClick={() => lookupLastVisit()}>
              {loading ? 'Looking up...' : 'Find Me'}
            </button>
            <button className="kiosk-nav-back" onClick={onBack}>← Back</button>
          </div>
        )}

        {/* FIRST NAME — multiple accounts on this phone */}
        {step === 'name' && (
          <div className="kiosk-screen kiosk-phone-screen kiosk-fade-in">
            <div className="kiosk-screen-title">What's your first name?</div>
            <div className="kiosk-screen-sub">We found more than one account with this number</div>
            <input
              className="kiosk-phone-input"
              type="text"
              placeholder="First name"
              value={nameInput}
              readOnly
            />
            <KioskKeypad
              mode="alpha"
              value={nameInput}
              onChange={setNameInput}
              maxLength={30}
            />
            {error && <div style={{ color: '#c44', fontSize: 13, textAlign: 'center' }}>{error}</div>}
            <button
              className="kiosk-btn-primary"
              disabled={!nameInput.trim() || loading}
              onClick={() => lookupLastVisit(nameInput.trim())}
            >
              {loading ? 'Looking up...' : 'Continue'}
            </button>
            <button className="kiosk-nav-back" onClick={() => { setError(null); setNameInput(''); setStep('phone'); }}>← Back</button>
          </div>
        )}

        {/* NO PREVIOUS VISIT FOUND */}
        {step === 'not_found' && (
          <div className="kiosk-screen kiosk-checkin-screen kiosk-fade-in" style={{ justifyContent: 'center', textAlign: 'center' }}>
            <div className="kiosk-screen-title">No previous visits found</div>
            <div className="kiosk-screen-sub" style={{ marginBottom: 24 }}>
              We couldn't find a past appointment for {guest?.firstName || 'you'} at this location.
            </div>
            <button className="kiosk-btn-primary" onClick={onBack}>Book a New Appointment</button>
          </div>
        )}

      </div>
      {showWarning && <IdleWarningModal countdown={countdown} onDismiss={dismissWarning} onReset={onBack} />}
    </div>
  );
}
